/**
 * A WordElementToken represents a part of a word which can be rendered with the same font and style.
 * WordElementTokens may be highlighted independently from other WordElementTokens.
 * WordElementTokens are contained in a WordToken.
 *
 */
export class WordTokenElement {
    readonly classType: string = 'WordElementToken';
    #text: string = '';
    #isHighlighted: boolean = false;
    #isBold: boolean = false;
    #isItalic: boolean = false;
    #isUnderline: boolean = false;
    #sourceWordReferenceIndex: number = -1;

    /**
     * Builds a WordElementToken with a given text and highlight status.
     */
    constructor(text: string, highlighted: boolean, sourceWordReferenceIndex?: number) {
        this.#text = text;
        this.#isHighlighted = highlighted;
        this.#sourceWordReferenceIndex = sourceWordReferenceIndex ?? -1;
    }

    /**
     * Gets the text of this token.
     */
    get text(): string {
        return this.#text;
    }

    /**
     * Set the text of the token.
     */
    set text(text: string) {
        this.#text = text;
    }

    /**
     * Returns true if the token should be highlighted, false otherwise.
     */
    get highlighted(): boolean {
        return this.#isHighlighted;
    }

    /**
     * Sets the highlight status of the token. Set this to true to highlight the token.
     */
    set highlighted(highlighted: boolean) {
        this.#isHighlighted = highlighted;
    }

    /**
     * Returns true if the token should be bold, false otherwise.
     */
    get bold(): boolean {
        return this.#isBold;
    }

    /**
     * Sets the bold status of the token.
     */
    set bold(value: boolean) {
        this.#isBold = value;
    }

    /**
     * Returns true if the token should be italic, false otherwise.
     */
    get italic(): boolean {
        return this.#isItalic;
    }

    /**
     * Sets the italic status of the token.
     */
    set italic(value: boolean) {
        this.#isItalic = value;
    }

    /**
     * Returns true if the token should be underlined, false otherwise.
     */
    get underline(): boolean {
        return this.#isUnderline;
    }

    /**
     * Sets the underline status of the token.
     */
    set underline(value: boolean) {
        this.#isUnderline = value;
    }

    /**
     * Reference index to the source text that this token is a part of.
     */
    get sourceWordReferenceIndex(): number {
        return this.#sourceWordReferenceIndex;
    }

    /**
     * Returns a new WordElementToken with a substring of the token's text
     *
     * @param start - zero-based index at which to begin extraction
     * @param end - extracts up to but not including end
     */
    slice(start: number, end: number): WordTokenElement {
        const clampedStart = Math.max(0, start);
        const clampedEnd = Math.min(end, this.#text.length);
        const text = this.#text.slice(clampedStart, clampedEnd);

        const element = new WordTokenElement(
            text,
            this.#isHighlighted,
            this.#sourceWordReferenceIndex,
        );
        element.bold = this.#isBold;
        element.italic = this.#isItalic;
        element.underline = this.#isUnderline;
        return element;
    }
}
